import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

interface SkillNameConflictDialogProps {
  open: boolean;
  /** The colliding name the user tried to use. */
  originalName: string;
  /** The dash-suffixed name "Keep both" will use. */
  suggestedName: string;
  /** Disables the actions while the resolving mutation is in flight. */
  isPending?: boolean;
  onCancel: () => void;
  onKeepBoth: () => void;
  /**
   * Replaces the existing skill's files in place (same skill id), mirroring
   * the file-record "Replace contents" option. Rendered only when provided —
   * flows without a replace primitive (e.g. restore from trash) omit it.
   */
  onReplaceContent?: () => void;
}

/**
 * Shared collision dialog for skill name conflicts, styled to match the
 * file-record FileConflictDialog (ghost Cancel on the left, actions on the
 * right, Keep both as the focused default). Used by restore-from-trash and
 * skill upload so all flows offer the same choices with identical wording.
 */
export function SkillNameConflictDialog({
  open,
  originalName,
  suggestedName,
  isPending = false,
  onCancel,
  onKeepBoth,
  onReplaceContent,
}: SkillNameConflictDialogProps) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onCancel();
      }}
    >
      <DialogContent className="sm:max-w-xl p-8 gap-6">
        <DialogHeader className="space-y-3">
          <DialogTitle className="text-lg leading-snug">
            &ldquo;{originalName}&rdquo; already exists in your skills.
          </DialogTitle>
          <DialogDescription className="text-sm leading-relaxed">
            {onReplaceContent ? (
              <>
                Do you want to replace the existing skill&rsquo;s files, or keep
                both versions? Keeping both saves the new skill as &ldquo;
                {suggestedName}&rdquo;.
              </>
            ) : (
              <>
                Do you want to keep both versions? The restored skill will be
                saved as &ldquo;{suggestedName}&rdquo;.
              </>
            )}
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="gap-2 flex-col-reverse sm:flex-row sm:justify-between sm:gap-0 pt-2">
          <Button variant="ghost" onClick={onCancel} disabled={isPending}>
            Cancel
          </Button>
          <div className="flex gap-2">
            {onReplaceContent && (
              <Button
                variant="outline"
                onClick={onReplaceContent}
                disabled={isPending}
              >
                Replace contents
              </Button>
            )}
            <Button autoFocus onClick={onKeepBoth} disabled={isPending}>
              Keep both
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
